import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_ZOOM = 2.5;
const SWIPE_THRESHOLD = 60;

/**
 * Full-screen image lightbox with:
 *   • Web: zoom buttons, arrows, photo counter, keyboard (Esc/←/→/+/-/0), wheel zoom
 *   • Mobile: pinch-to-zoom, double-tap zoom, drag-to-pan when zoomed, swipe when at 1x
 * Renders via portal to escape parent stacking contexts.
 */
export default function ImageLightbox({ images, index, onIndexChange, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);

  // Interaction refs (mutable, not reactive)
  const lastTapRef = useRef(0);
  const pinchStartRef = useRef(null); // { dist, scale, midX, midY }
  const dragStartRef = useRef(null);  // { x, y, offX, offY, mode: 'pan'|'swipe' }
  const swipeStartXRef = useRef(null);

  const src = images[index];
  const total = images.length;

  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);

  const clampOffset = useCallback((next, s) => {
    const el = stageRef.current;
    if (!el || s <= 1) return { x: 0, y: 0 };
    const maxX = (el.clientWidth * (s - 1)) / 2;
    const maxY = (el.clientHeight * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  }, []);

  const zoomBy = useCallback((delta, focus = null) => {
    setScale((s) => {
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta));
      // Keep focal point roughly stable when zooming with the wheel/double-tap
      if (focus && stageRef.current) {
        const rect = stageRef.current.getBoundingClientRect();
        const cx = focus.x - rect.left - rect.width / 2;
        const cy = focus.y - rect.top - rect.height / 2;
        const k = next / s - 1;
        setOffset((o) => clampOffset({ x: o.x - cx * k, y: o.y - cy * k }, next));
      } else if (next === 1) {
        setOffset({ x: 0, y: 0 });
      }
      return next;
    });
  }, [clampOffset]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    reset();
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange, reset]);

  const next = useCallback(() => {
    if (total <= 1) return;
    reset();
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange, reset]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") zoomBy(0.5);
      else if (e.key === "-") zoomBy(-0.5);
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, zoomBy, reset]);

  // Body scroll lock
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Wheel zoom (web) — attach as native non-passive listener so preventDefault() actually blocks page scroll
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 0.3 : -0.3, { x: e.clientX, y: e.clientY });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  // Touch gestures — pinch, double-tap, pan, swipe
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      pinchStartRef.current = {
        dist: Math.hypot(dx, dy),
        scale,
        midX: (t1.clientX + t2.clientX) / 2,
        midY: (t1.clientY + t2.clientY) / 2,
      };
      dragStartRef.current = null;
      swipeStartXRef.current = null;
      return;
    }
    if (e.touches.length === 1) {
      const t = e.touches[0];
      // Double-tap detection
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        lastTapRef.current = 0;
        if (scale > 1) reset();
        else {
          setScale(DOUBLE_TAP_ZOOM);
          if (stageRef.current) {
            const rect = stageRef.current.getBoundingClientRect();
            const cx = t.clientX - rect.left - rect.width / 2;
            const cy = t.clientY - rect.top - rect.height / 2;
            const k = DOUBLE_TAP_ZOOM - 1;
            setOffset(clampOffset({ x: -cx * k, y: -cy * k }, DOUBLE_TAP_ZOOM));
          }
        }
        return;
      }
      lastTapRef.current = now;

      if (scale > 1) {
        dragStartRef.current = { x: t.clientX, y: t.clientY, offX: offset.x, offY: offset.y, mode: "pan" };
      } else {
        swipeStartXRef.current = t.clientX;
      }
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const nextScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, pinchStartRef.current.scale * (dist / pinchStartRef.current.dist)),
      );
      setScale(nextScale);
      if (nextScale === 1) setOffset({ x: 0, y: 0 });
      return;
    }
    if (e.touches.length === 1 && dragStartRef.current?.mode === "pan") {
      e.preventDefault();
      const t = e.touches[0];
      const dx = t.clientX - dragStartRef.current.x;
      const dy = t.clientY - dragStartRef.current.y;
      setOffset(clampOffset({ x: dragStartRef.current.offX + dx, y: dragStartRef.current.offY + dy }, scale));
    }
  };

  const onTouchEnd = (e) => {
    // End of pinch
    if (pinchStartRef.current && e.touches.length < 2) pinchStartRef.current = null;

    // Swipe navigation only at 1x
    if (swipeStartXRef.current != null && scale === 1) {
      const endX = (e.changedTouches?.[0]?.clientX) ?? swipeStartXRef.current;
      const delta = endX - swipeStartXRef.current;
      swipeStartXRef.current = null;
      if (delta > SWIPE_THRESHOLD) prev();
      else if (delta < -SWIPE_THRESHOLD) next();
    }
    if (e.touches.length === 0) dragStartRef.current = null;
  };

  // Mouse drag pan (web) when zoomed
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragStartRef.current = { x: e.clientX, y: e.clientY, offX: offset.x, offY: offset.y, mode: "pan" };
    const move = (ev) => {
      if (!dragStartRef.current) return;
      const dx = ev.clientX - dragStartRef.current.x;
      const dy = ev.clientY - dragStartRef.current.y;
      setOffset(clampOffset({ x: dragStartRef.current.offX + dx, y: dragStartRef.current.offY + dy }, scale));
    };
    const up = () => {
      dragStartRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const overlay = (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col select-none"
      onClick={onBackdropClick}
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 md:px-6 py-3 text-white pointer-events-none">
        <div
          className="pointer-events-auto rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-xs font-semibold tabular-nums"
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            title="Reset zoom (0)"
            data-testid="lightbox-reset"
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="lightbox-close"
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        className="flex-1 relative overflow-hidden touch-none"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          data-testid="lightbox-image"
          className="absolute inset-0 m-auto max-w-full max-h-full object-contain will-change-transform"
          style={{
            transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
            transition: dragStartRef.current || pinchStartRef.current ? "none" : "transform 180ms ease-out",
            cursor: scale > 1 ? "grab" : "zoom-in",
          }}
          onDoubleClick={(e) => {
            if (scale > 1) reset();
            else zoomBy(DOUBLE_TAP_ZOOM - 1, { x: e.clientX, y: e.clientY });
          }}
        />

        {/* Prev / next arrows — hidden if only one image */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous"
              data-testid="lightbox-prev"
              className="hidden md:grid absolute left-4 top-1/2 -translate-y-1/2 place-items-center w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next"
              data-testid="lightbox-next"
              className="hidden md:grid absolute right-4 top-1/2 -translate-y-1/2 place-items-center w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Bottom bar — zoom controls + mobile nav */}
      <div className="relative z-10 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-center gap-2 px-4 py-3 pointer-events-none">
          <div className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur px-1.5 py-1 text-white">
            {total > 1 && (
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                data-testid="lightbox-prev-mobile"
                className="md:hidden p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => zoomBy(-0.5)}
              disabled={scale <= MIN_SCALE}
              aria-label="Zoom out"
              data-testid="lightbox-zoom-out"
              className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40 transition-colors"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span
              className="px-3 text-xs font-semibold tabular-nums w-14 text-center"
              data-testid="lightbox-scale"
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={() => zoomBy(0.5)}
              disabled={scale >= MAX_SCALE}
              aria-label="Zoom in"
              data-testid="lightbox-zoom-in"
              className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40 transition-colors"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            {total > 1 && (
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                data-testid="lightbox-next-mobile"
                className="md:hidden p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(overlay, document.body);
}
